import React from 'react'

import ICSSubmitButton from '../../../components/ICSSubmitButton/ICSSubmitButton'
import { useEffect } from 'react'
import { useForm, Control, useFormState } from 'react-hook-form'
import { Container, Row, Col, Form } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import { LoadIndicator } from 'devextreme-react'
import { createDataset } from '../../../services/endpoints/datasets'
import useAsyncService from '../../../hooks/useAsyncService'
import ComponentBox from '../../../components/ComponentBox/ComponentBox'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { IpaDatasetFilter } from '../types'
import ICSVehiclePicker from '../../../components/ICSVehiclePicker/ICSVehiclePicker'
import ICSFileExtensionPicker from '../../../components/ICSFileExtensionPicker/SimpleFileExtensionPicker'
import CustomTagBox from '../../../components/ICSSimpleTagBox/ICSSimpleTagBox'

import './DatasetsFilterBox.css'

const predefinedExtensions = [
  'ASC',
  'BLF',
  'CSV',
  'DAT',
  'DB',
  'IMG',
  'Log File .CSV',
  'MAT',
  'MF4',
  'PCAP',
  'PCAPNG',
  'VSB',
  'XML'
] // Temporarily hardcoding these here until we develop a method of retrieving these via the API

export interface _DatasetFilterValues {
  vehiclePicker: {
    fleets: number[]
    vehicles: number[]
  }
  collections: string[]
  fileTypes: string[]
  name: string
  description: string
}

const DatasetsFilterBox: React.FC = () => {
  const { handleSubmit, control, register } = useForm<_DatasetFilterValues>({
    mode: 'onChange',
    defaultValues: {
      vehiclePicker: { fleets: [], vehicles: [] },
      collections: [],
      fileTypes: [],
      name: '',
      description: ''
    }
  })

  const navigate = useNavigate()
  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: createDataset,
      delay: 500,
      initialArgs: null
    })

  useEffect(() => {
    if (serviceValue) {
      navigate('/ui2/datasets/')
    }
  }, [serviceValue])

  const generate = async (data: _DatasetFilterValues) => {
    const payload = _formatDatasetBody(data)
    setServiceArgs(payload)
  }

  const getBody = () => {
    if (serviceLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh'
          }}
        >
          <LoadIndicator className="load-indicator" height={100} width={100} />
        </div>
      )
    }
    if (serviceError) {
      return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
    }

    return (
      <Form onSubmit={handleSubmit(generate)}>
        <ComponentBox
          isCustom
          cardClass="groups-results-container"
          cardBodyId="groupsCardBody"
          title="Dynamic Dataset Filter Criteria"
          accordion
        >
          <Row>
            <Col xs={12} md={6}>
              <ICSVehiclePicker<_DatasetFilterValues, 'vehiclePicker'>
                name="vehiclePicker"
                control={control}
                required={false}
              />
            </Col>
            <Col xs={12} md={6}>
              <div className="spacer">
                <h4>Collection or File Name</h4>
                <CustomTagBox<_DatasetFilterValues, 'collections'>
                  name="collections"
                  control={control}
                  placeholder="Enter collection or file name and press Enter"
                />
              </div>
              <div className="spacer">
                <h4>File Extensions</h4>
                <ICSFileExtensionPicker
                  name="fileTypes"
                  control={control}
                  extensionsList={predefinedExtensions}
                />
              </div>
              <div className="nameBox">
                <div className="spacer">
                  <h4>Dataset Name</h4>
                  <input
                    className="form-control"
                    placeholder="Enter dataset name"
                    {...register('name', {
                      required: true
                    })}
                  />
                </div>
                <div className="spacer">
                  <h4>Dataset Description</h4>
                  <textarea
                    className="form-control"
                    placeholder="Enter description"
                    rows={3}
                    {...register('description')}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <div className="dataSearchButtonsRow justify-content-end row">
            <div className="col-sm-auto dataSearchButtonCol">
              <ICSSubmitButton
                className="dataSearchButton glyphicons glyphicons-floppy-disk"
                control={control}
              >
                Create Dynamic Dataset
              </ICSSubmitButton>
            </div>
          </div>
        </ComponentBox>
      </Form>
    )
  }

  return <Container fluid>{getBody()}</Container>
}

export default DatasetsFilterBox

export const _formatDatasetBody = (data: _DatasetFilterValues) => {
  const filterList: IpaDatasetFilter[] = []

  if (data.fileTypes.length > 0) {
    filterList.push({ type: 'Extension', value: data.fileTypes })
  }

  if (data.collections.length > 0) {
    filterList.push({ type: 'Collection', value: data.collections })
  }

  if (data.vehiclePicker.fleets.length > 0) {
    filterList.push({ type: 'Fleet', value: data.vehiclePicker.fleets })
  }

  if (data.vehiclePicker.vehicles.length > 0) {
    filterList.push({ type: 'Vehicle', value: data.vehiclePicker.vehicles })
  }

  return {
    name: data.name,
    description: data.description,
    type: 'dynamic',
    filters: filterList,
    upload_file_version_id: []
  }
}
